import * as Sentry from '@sentry/browser';
import moment from 'moment-timezone';
import hateoas from 'viralstyle-ui/src/hateoas';
import storage from 'viralstyle-ui/src/storage';
import requestService from 'viralstyle-ui/src/requestService';
import { get as _get } from 'lodash/get';

import apiService from '../_base-api';
import config from 'config';
import dataURItoBlob from 'utils/data-uri-to-blob';
import { statusEnum } from 'domains/campaigns/_campaigns-enum';

const TOKEN_SUFFIX = config.ENVIRONMENT !== 'Production' ? '-dev' : '',
  JWT_TOKEN_NAME = `client-jwt${TOKEN_SUFFIX}`,
  OAUTH_TOKEN_NAME = `oauth${TOKEN_SUFFIX}`;

export const async = async ({ commit, dispatch, state }, func) => {
  let promiseObj = {
    id: state.promiseCount,
    promise: func()
  };

  commit('addPromise', promiseObj);

  try {
    let response = await promiseObj.promise;

    commit('removePromise', promiseObj);
    commit('toggleLogin', false);

    return response;
  } catch (error) {
    commit('removePromise', promiseObj);

    // only log the error if its not an invalid login, no need to flood sentry with bad login attempts
    if (error.requiresLogin) {
      commit('toggleLogin', true);
      // silently fail
    } else {
      dispatch('logError', error);

      // set this flag so on the global exception catch all, we dont log this
      error.logged = true;
    }

    throw error;
  }
};

export const getCurrentUserId = async ({ state, dispatch, commit }, refreshCurrentUser) => {
  let response;

  try {
    response = await dispatch('async', () => apiService.getCurrentUserId());

    commit('setCurrentUserVariables', response);

  } catch (e) {
    if (!_get(e, 'requiresLogin')) {
      window.location.href = `${config.APP_PROTOCOL}://${config.APP_URL}`;
    }

    throw e;
  }

  if (!state.currentUser || (state.currentUser && state.currentUser.isRoot && refreshCurrentUser)) {
    response = await dispatch('getCurrentUserDetails', refreshCurrentUser);

    commit('setState', { object: state.currentUser, property: 'isRoot', value: true });
  }

  return response;
};

export const getCurrentUserDetails = async ({ state, dispatch, commit }, force) => {
  if (!state.currentUser || force) {
    let response = await dispatch('async', () => apiService.getCurrentUserDetails());

    commit('setCurrentUser', response.data);

    return response;
  }
};

export const logout = async ({ state, dispatch }) => {
  if (state.isImpersonation) {
    await dispatch('logoutImpersonation', true);
  }

  if (config.RAVEN_URL) {
    // clear sentry user
    Sentry.configureScope(scope => scope.setUser());
  }

  storage.removeDomainCookie(JWT_TOKEN_NAME);
  storage.remove('client-hash');
  storage.removeDomainCookie(OAUTH_TOKEN_NAME);
  storage.set('selected-date', { type: 'Today', start: null, end: null }, { storages: ['session'] });

  window.location.href = `${config.APP_PROTOCOL}://${config.APP_URL}/logout`;
};

export const getDynamicProductFeed = async ({ state, dispatch, commit }, { collection, shortUrls, includeAllProducts }) => {
  let url = state.currentUser._links.download_product_feed_offline.href,
    start,
    end,
    filters = [];

  if (collection) {
    start = collection.start || moment().tz(state.currentUser.timezone).startOf('day');
    end = collection.end || moment().tz(state.currentUser.timezone).endOf('day');

    filters.push(`sort=${collection.sortFilter}`);

    if (collection.productFilter !== -1) {
      filters.push(`filters[]=product_type:${collection.productFilter}`);
    }

    switch (collection.statusFilter) {
      case statusEnum.ACTIVE:
        filters.push('filters[]=ACTIVE');
        break;
      case statusEnum.STARRED:
        filters.push('filters[]=STARRED');
        break;
      case statusEnum.ENDED:
        filters.push('filters[]=ENDED');
        break;
      case statusEnum.FAILED:
        filters.push('filters[]=FAILED');
        break;
    }

    if (collection.domainFilter && collection.domainFilter != -1) {
      filters.push(`filters[]=whitelabel_domain:${collection.domainFilter}`);
    }

    if (collection.searchQuery) {
      filters.push(`filters[]=relates_to:${encodeURIComponent(collection.searchQuery)}`);
    }

    if (collection.createdByFilter && collection.createdByFilter != -1) {
      filters.push(`filters[]=launched_by:${collection.createdByFilter}`);
    }
  }

  if (start && start !== 'all') {
    filters.push(`filters[]=statistics_date_range:${start.toISOString()}|${end.toISOString()}`);
  }

  if (shortUrls) {
    filters.push('short=true');
  }

  if (includeAllProducts) {
    filters.push('all_products=true');
  }

  if (filters.length) {
    url = `${url}?${filters.join('&')}`;
  }

  let response = await dispatch('get', url);

  commit('addNotification', response.data.message);

  return response;
};

export const downloadFile = async ({ dispatch }, { response, fileName }) => {
  let blob = dataURItoBlob(response.file)

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, fileName);
  } else {
    let hiddenEl = document.createElement('a');
    hiddenEl.href = URL.createObjectURL(blob);
    hiddenEl.download = fileName;

    document.body.appendChild(hiddenEl);
    hiddenEl.click();
    document.body.removeChild(hiddenEl);
  }
};

export const getMyOrders = async ({ state, dispatch, commit }) => {
  let response = await requestService.get(`${config.CUSTOMER_API_URL}/customer/${storage.get('client-hash')}/orders`);

  if (response.data._embedded.items.length) commit('updateState', { object: state, property: 'showMyOrdersLink', value: true });

  return response;
};

export const updatePersistentFilters = async ({ state, dispatch, commit }) => {
  let collection = state.campaigns.campaigns.all,
    payload = {
      sort_column: collection.sortFilter,
      display_type: state.campaigns.displayType,
    },
    fetchDetails = false,
    isAllTime = collection.dateType === 'All Time';

  if (collection.domainFilter !== -1) {
    payload.domain_id = collection.domainFilter;
  }

  if (collection.productFilter !== -1) {
    payload.product_type_id = collection.productFilter;
  }

  if (collection.createdByFilter !== -1) {
    payload.created_by_id = collection.createdByFilter;
  }

  if (collection.statusFilter !== 'All') {
    payload.status = collection.statusFilter;
  }

  if (collection.dateType === 'All Time' || collection.dateType === 'Last 14 Days' || collection.end.diff(collection.start, 'days') > 30) {
    if (state.previousPersistableDate.type === 'Custom') {
      payload.custom_date_range = `${state.previousPersistableDate.start.toISOString()},${state.previousPersistableDate.end.toISOString()}`;
    } else {
      payload.date_range_preset = state.previousPersistableDate.type.toLowerCase();
    }
  } else if (collection.dateType === 'Custom') {
    payload.custom_date_range = `${collection.start.format('M/D/YY')},${collection.end.format('M/D/YY')}`;
  } else {
    payload.date_range_preset = collection.dateType.toLowerCase();
  }

  let response = await dispatch('put', { apiUrl: state.persistentFilters.updateUrl, payload }),
    isRoot = state.currentUser.isRoot;

  await dispatch('getCurrentUserId', true);

  if (isAllTime) {
    commit('updateState', { object: collection, property: 'dateType', value: 'All Time' });

    commit('updateState', { object: collection, property: 'start', value: 'all' });

    commit('updateState', { object: collection, property: 'end', value: 'all' });
  }

  if (isRoot) {
    commit('setState', { object: state.currentUser, property: 'isRoot', value: true });
  }

  return response;
};

export const logoutImpersonation = async ({ state, dispatch, commit }, noClose) => {
  let response = await dispatch('del', state.logoutImpersonation);

  if (!noClose) {
    window.close();
  }

  return response;
};